/**
 * Charm personalization: Personalize Trigger, Personalization Modal, and PDP
 * Set Summary Cards.
 *
 * UI/state only — network access is limited to lazily fetching the Master
 * Charms Product's `.js` JSON (Story 2.4, AD-6, cached at module scope, not
 * per-instance, so multiple block instances on one page share one fetch).
 * All cart-add/rollback/cart-render concerns live in the sibling module
 * `personalize-cart.js` instead (AD-7 module boundary) — this file never
 * issues a `/cart/add.js` call itself.
 *
 * Extends the theme's `DialogComponent` (AD-7 / FR-9) so Escape, outside-click
 * dismiss, native focus trap (`showModal`), focus-return on close, and the
 * body scroll-lock animation path are inherited — not reimplemented. Nesting
 * a separate `<dialog-component>` is intentionally avoided: `Component`'s
 * `#isDescendant` / `getClosestComponent` rules would assign every nested
 * `ref` to the inner dialog host and break this element's `requiredRefs`.
 *
 * @see _bmad-output/planning-artifacts/architecture/architecture-Shopify-2026-08-10/architecture.md §4
 * @see _bmad-output/planning-artifacts/architecture/architecture-Shopify-2026-08-10/ARCHITECTURE-SPINE.md AD-6, AD-7
 */
import { DialogComponent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';

/**
 * AD-6: module-scoped, URL-keyed cache of Master Charms Product `.js` fetches.
 * Values are Promises so concurrent first-opens (or two block instances) share
 * one in-flight request and never re-hit the network for the same URL.
 * @type {Map<string, Promise<object>>}
 */
const charmsProductByUrl = new Map();

/** Canonical Symbol option order (FR-1 / FR-14): A–Z, Heart, Star, Flower. */
const CHARM_SYMBOL_ORDER = [
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
  'Heart',
  'Star',
  'Flower',
];

/** Fallback swatch fills when a color has no featured image yet. */
const CHARM_COLOR_FALLBACK_CSS = {
  Red: '#e53935',
  Yellow: '#fdd835',
};

/**
 * @param {string|undefined|null} value
 * @returns {string}
 */
function safeDecodeURIComponent(value) {
  if (typeof value !== 'string' || !value) return '';
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

/**
 * @param {unknown} data
 * @returns {asserts data is CharmsProductJson}
 */
function assertCharmsProductJson(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid charms product JSON');
  }
  const product = /** @type {CharmsProductJson} */ (data);
  if (!Array.isArray(product.variants) || product.variants.length === 0) {
    throw new Error('Charms product JSON missing variants');
  }
  const charmColor = product.options?.find((option) => option.name === 'Charm Color');
  if (!charmColor || !Array.isArray(charmColor.values) || charmColor.values.length === 0) {
    throw new Error('Charms product JSON missing Charm Color option values');
  }
}

/**
 * @typedef {object} PersonalizeCharm
 * @property {string} symbol
 * @property {string} color
 * @property {string} variantId
 * @property {string} [image]
 * @property {number} [price]
 */

/**
 * @typedef {object} CharmsProductJson
 * @property {Array<{ name: string, position: number, values: string[] }>} [options]
 * @property {Array<{
 *   id: number|string,
 *   option1: string,
 *   option2: string,
 *   available: boolean,
 *   price: number,
 *   featured_image?: { src?: string }|null,
 * }>} variants
 */

/**
 * @typedef {object} PersonalizeSet
 * @property {string} setId
 * @property {PersonalizeCharm[]} charms
 * @property {number} total
 * @property {boolean} cartReadyAtCommit AD-7 liveness snapshot at commit time (Story 2.6/2.7)
 */

/**
 * @typedef {object} PersonalizeRefs
 * @property {HTMLButtonElement} triggerButton
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [colorPreviewRow]
 * @property {HTMLElement} [strapPreview]
 * @property {HTMLElement} [counter]
 * @property {HTMLButtonElement} [clearButton]
 * @property {HTMLElement} [charmColorPicker]
 * @property {HTMLElement} tileGrid
 * @property {HTMLElement} [footerPriceLine]
 * @property {HTMLElement} [addToSetHelper]
 * @property {HTMLButtonElement} addToSetButton
 * @property {HTMLElement} [setSummaryList]
 * @extends {DialogComponent}
 */
export class PersonalizeCharmsComponent extends DialogComponent {
  requiredRefs = ['triggerButton', 'dialog', 'tileGrid', 'addToSetButton'];

  /** @type {PersonalizeCharm[]} in-progress selection, ordered (Story 2.4/2.5) */
  #inProgress = [];

  /** @type {PersonalizeSet[]} committed Charm Sets (Story 2.6/2.7) */
  #committedSets = [];

  /** @type {string|undefined} the Host Product's id — every dispatched event is tagged with this (AD-7) */
  #hostProductId;

  /** @type {string|undefined} currently active Charm Color in the modal (Story 2.4) */
  #activeColor;

  /**
   * Resolved Master Charms Product JSON for this instance (after AD-6 load).
   * The network cache itself lives in `charmsProductByUrl`, not here.
   * @type {CharmsProductJson|null}
   */
  #charmsProduct = null;

  /** @type {string|undefined} Host Product Color option value mirrored in the preview (Story 2.3 / FR-10) */
  #collarColor;

  /** @type {MutationObserver|undefined} */
  #placeObserver;

  /** @type {number|undefined} */
  #uiSyncTimer;

  /** @type {Array<() => void>} */
  #uiSyncQueue = [];

  connectedCallback() {
    super.connectedCallback();
    this.#hostProductId = this.closest('product-form-component')?.dataset.productId ?? this.dataset.hostProductId;
    this.#collarColor = this.dataset.selectedCollarColor || undefined;

    // Wishlist app injects into the product form asynchronously — place this block
    // between wishlist and quantity/Add to cart once the form is ready.
    this.#placeBlockInBuyForm();
  }

  disconnectedCallback() {
    this.#placeObserver?.disconnect();
    if (this.#uiSyncTimer !== undefined) {
      window.clearTimeout(this.#uiSyncTimer);
      this.#uiSyncTimer = undefined;
    }
    // Flush any pending post-commit UI work before teardown.
    if (this.#uiSyncQueue.length) {
      const batch = this.#uiSyncQueue.splice(0);
      for (const fn of batch) fn();
    }
    super.disconnectedCallback();
  }

  // Public methods, bound from Liquid via the on:click="/methodName" convention
  // (component.js) as each is wired up by its owning story.

  /** Opens the Personalization Modal. Story 2.2 (FR-9); lazy-loads charms on first open (Story 2.4 / AD-6). */
  async openModal() {
    if (this.dataset.charmsAvailable === 'false') return;
    if (this.#committedSets.length >= this.#maxSets()) return;

    // FR-10: one-way seed only — copy Host Product Color into the modal at open.
    // Modal stays independent afterward (no variant:update listener).
    // In-modal selectCollarColor may override preview/strap without PDP writeback.
    this.#syncCollarColorPreview(this.#readCurrentVariantFromPdp());
    this.showDialog();
    // Show 0-charm footer/counter immediately; refresh again after charms load.
    this.#syncInProgressUi();

    try {
      await this.#ensureCharmsProductLoaded();
      const colors = this.#charmColorValues();
      if (!colors.length) {
        this.#renderCharmsLoadError();
        return;
      }
      if (!this.#activeColor) {
        this.#activeColor = colors[0];
      }
      this.#renderCharmColorPicker();
      this.#syncInProgressUi();
    } catch (error) {
      console.error('[personalize-charms] Failed to load Master Charms Product', error);
      this.#renderCharmsLoadError();
    }
  }

  /** Closes the Personalization Modal. Story 2.2 (FR-9). */
  closeModal() {
    this.closeDialog();
  }

  /**
   * Switches the modal's active Charm Color and re-renders the tile grid.
   * Does not discard `#inProgress` charms of other colors (FR-13). Story 2.4.
   * @param {string} colorValue
   */
  selectCharmColor(colorValue) {
    const color = safeDecodeURIComponent(colorValue);
    if (!color || color === this.#activeColor) return;
    this.#activeColor = color;
    // Recolor every in-progress charm so the strap preview stays consistent.
    this.#recolorInProgressCharms(color);
    // Defer DOM rebuild so DialogComponent's outside-click handler still sees
    // the original event.target inside <dialog> during this click's bubble phase.
    this.#scheduleUiSync(() => {
      this.#renderCharmColorPicker();
      this.#renderTileGrid();
      this.#syncInProgressUi();
    });
  }

  /**
   * Updates every in-progress charm to the given color, resolving the new
   * variant for each symbol. Charms whose symbol has no variant in the new
   * color are left unchanged (graceful fallback). Inverse of FR-13: color
   * switch re-colors the existing selection rather than discarding it.
   * @param {string} color
   */
  #recolorInProgressCharms(color) {
    if (!this.#charmsProduct || !this.#inProgress.length) return;
    this.#inProgress = this.#inProgress.map((charm) => {
      const variant = this.#variantFor(charm.symbol, color);
      if (!variant) return charm; // symbol not available in new color — keep as-is
      const image =
        typeof variant.featured_image?.src === 'string' ? variant.featured_image.src : undefined;
      return {
        ...charm,
        color,
        variantId: String(variant.id),
        image,
        price: variant.price,
      };
    });
  }

  /**
   * Updates only the in-modal Collar Color Preview + strap graphic (FR-10).
   * Seeded at open from the PDP; afterward the modal is independent.
   * Does not change the Host Product variant on the PDP.
   * @param {string} colorValue
   */
  selectCollarColor(colorValue) {
    const color = safeDecodeURIComponent(colorValue);
    if (!color) return;
    this.#syncCollarColorPreview(this.#stubVariantForCollarColor(color));
  }

  /**
   * Adds a tile to the in-progress selection when under Max Charms Limit and in stock.
   * FR-31: repeats allowed — no per-Symbol dedup. Strap/counter UI is Story 2.5.
   * @param {string} symbol
   */
  selectTile(symbol) {
    const decoded = safeDecodeURIComponent(symbol);
    if (!decoded || !this.#activeColor || !this.#charmsProduct) return;
    if (this.#inProgress.length >= this.#maxCharms()) return;

    const variant = this.#variantFor(decoded, this.#activeColor);
    if (!variant?.available) return;

    const image =
      typeof variant.featured_image?.src === 'string' ? variant.featured_image.src : undefined;

    this.#inProgress.push({
      symbol: decoded,
      color: this.#activeColor,
      variantId: String(variant.id),
      image,
      price: variant.price,
    });

    this.#scheduleUiSync(() => this.#syncInProgressUi());
  }

  /**
   * Removes one charm from the in-progress selection by index. Story 2.5 (FR-11).
   * Defers DOM rebuild via `#scheduleUiSync` so DialogComponent outside-click
   * does not see a detached × as "outside". Neutralizes strap × controls first
   * so rapid clicks cannot hit stale `on:click` indices before the sync.
   * @param {number|string} index
   */
  removeInProgressCharm(index) {
    const i = typeof index === 'number' ? index : Number(index);
    if (!Number.isInteger(i) || i < 0 || i >= this.#inProgress.length) return;
    this.#inProgress.splice(i, 1);
    this.#neutralizeStrapRemoveControls();
    this.#scheduleUiSync(() => this.#syncInProgressUi());
  }

  /**
   * Clears the entire in-progress selection with no confirmation. Story 2.5 (FR-12).
   * Same deferred sync + neutralize as remove — keeps modal open and blocks stale ×.
   */
  clearSelection() {
    if (this.#inProgress.length === 0) return;
    this.#inProgress = [];
    this.#neutralizeStrapRemoveControls();
    this.#scheduleUiSync(() => this.#syncInProgressUi());
  }

  /** Commits the in-progress selection as a new Charm Set. Story 2.6 (FR-15, AD-7). */
  addToSet() {
    if (this.#inProgress.length === 0) return;
    if (this.#committedSets.length >= this.#maxSets()) return;

    const charms = Object.freeze(this.#inProgress.map((charm) => Object.freeze({ ...charm })));
    const total = charms.reduce((sum, charm) => sum + (Number(charm.price) || 0), 0);
    // Snapshot liveness BEFORE dispatch so event listeners and #committedSets
    // see the same commit-time value (AD-7 / Story 2.7).
    const cartReadyAtCommit = document.documentElement.dataset.personalizeCartReady === 'true';

    /** @type {PersonalizeSet} */
    const set = Object.freeze({
      setId: crypto.randomUUID(),
      charms,
      total,
      cartReadyAtCommit,
    });

    this.#committedSets.push(set);
    this.#dispatchSetsChanged();

    this.#inProgress = [];
    this.closeModal();
    // Enforce + cards synchronously so Max Sets cannot be bypassed before setTimeout(0).
    this.#renderSetSummaryList();
    this.#enforceMaxSetsLimit();
    this.#scheduleUiSync(() => this.#syncInProgressUi());
  }

  /** Deletes a previously committed Charm Set by id, independent of any other set. Story 2.7 (FR-17). */
  deleteSet(setId) {
    const id = safeDecodeURIComponent(String(setId ?? ''));
    if (!id) return;

    const index = this.#committedSets.findIndex((set) => set.setId === id);
    if (index < 0) return;

    this.#committedSets.splice(index, 1);
    this.#dispatchSetsChanged();
    this.#renderSetSummaryList();
    this.#enforceMaxSetsLimit();
  }

  /**
   * Clears every committed Charm Set on this Host (Set Summary Cards + event).
   * Called by `personalize-cart.js` after a successful guarded Add to Cart (FR-24).
   */
  clearPendingSets() {
    if (this.#committedSets.length === 0) return;
    this.#committedSets = [];
    this.#dispatchSetsChanged();
    this.#renderSetSummaryList();
    this.#enforceMaxSetsLimit();
  }

  // Internal helpers, implemented by their owning story.

  /**
   * AD-6: lazily fetches and module-caches the Charms product JSON, keyed by URL.
   * Story 2.4.
   */
  async #ensureCharmsProductLoaded() {
    if (this.#charmsProduct) return this.#charmsProduct;

    const baseUrl = this.dataset.charmsProductUrl;
    if (!baseUrl) throw new Error('Missing data-charms-product-url');

    const fetchUrl = new URL(`${baseUrl}.js`, window.location.origin).href;
    let pending = charmsProductByUrl.get(fetchUrl);
    if (!pending) {
      pending = fetch(fetchUrl, { credentials: 'same-origin' }).then(async (response) => {
        if (!response.ok) {
          throw new Error(`Charms product fetch failed (${response.status})`);
        }
        const data = await response.json();
        assertCharmsProductJson(data);
        return /** @type {CharmsProductJson} */ (data);
      });
      charmsProductByUrl.set(fetchUrl, pending);
    }

    try {
      this.#charmsProduct = /** @type {CharmsProductJson} */ (await pending);
    } catch (error) {
      // Allow a later open to retry after a transient failure.
      charmsProductByUrl.delete(fetchUrl);
      throw error;
    }

    return this.#charmsProduct;
  }

  /** @returns {number} */
  #maxCharms() {
    const parsed = Number(this.dataset.maxCharms);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 8;
  }

  /** @returns {number} */
  #maxSets() {
    const parsed = Number(this.dataset.maxSets);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
  }

  /** @returns {string[]} */
  #charmColorValues() {
    const fromJson = this.#charmsProduct?.options?.find((option) => option.name === 'Charm Color')?.values;
    if (Array.isArray(fromJson) && fromJson.length) return fromJson;

    const fromData = this.dataset.charmsColorOptions;
    if (fromData) {
      try {
        const parsed = JSON.parse(fromData);
        if (Array.isArray(parsed)) {
          return parsed.map((value) => String(value).trim()).filter(Boolean);
        }
      } catch {
        // Legacy comma-joined fallback from earlier block markup.
      }
      return fromData
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }
    return [];
  }

  /**
   * @param {{ option1?: string, option2?: string, option3?: string }} variant
   * @param {string} optionName
   * @returns {string|undefined}
   */
  #variantOptionValue(variant, optionName) {
    const option = this.#charmsProduct?.options?.find((entry) => entry.name === optionName);
    if (option?.position === 1) return variant.option1;
    if (option?.position === 2) return variant.option2;
    if (option?.position === 3) return variant.option3;
    // Launch catalog fallback: Symbol = option1, Charm Color = option2.
    if (optionName === 'Symbol') return variant.option1;
    if (optionName === 'Charm Color') return variant.option2;
    return undefined;
  }

  /**
   * @param {string} symbol
   * @param {string} color
   */
  #variantFor(symbol, color) {
    return this.#charmsProduct?.variants.find(
      (variant) =>
        this.#variantOptionValue(variant, 'Symbol') === symbol &&
        this.#variantOptionValue(variant, 'Charm Color') === color
    );
  }

  /** @returns {string} */
  #outOfStockLabel() {
    return this.dataset.i18nOutOfStock || 'Out of Stock';
  }

  /**
   * @param {string} symbol
   * @param {string} color
   * @param {boolean} available
   */
  #tileAriaLabel(symbol, color, available) {
    const template = this.dataset.i18nTileAria || '__SYMBOL__ charm, __COLOR__';
    const base = template.replaceAll('__SYMBOL__', symbol).replaceAll('__COLOR__', color);
    return available ? base : `${base}, ${this.#outOfStockLabel()}`;
  }

  /**
   * @param {string} color
   * @returns {string} inline style for the picker swatch face
   */
  #charmColorSwatchStyle(color) {
    const variant = this.#charmsProduct?.variants.find(
      (entry) => this.#variantOptionValue(entry, 'Charm Color') === color && entry.featured_image?.src
    );
    const src = variant?.featured_image?.src;
    if (src) {
      return `background-image:url(${JSON.stringify(src)});background-size:cover;background-position:center;`;
    }
    const fallback = CHARM_COLOR_FALLBACK_CSS[/** @type {keyof typeof CHARM_COLOR_FALLBACK_CSS} */ (color)];
    return `background-color:${fallback || 'rgb(var(--color-foreground-rgb) / 0.2)'};`;
  }

  /** Interactive Charm Color picker (FR-13). Distinct BEM from Collar Color Preview. Story 2.4. */
  #renderCharmColorPicker() {
    const host = this.refs.charmColorPicker;
    if (!(host instanceof HTMLElement) || !this.#charmsProduct) return;

    const colors = this.#charmColorValues();
    const label = this.dataset.i18nCharmColorLabel || 'Charm Color';
    const labelId = `PersonalizeCharmsCharmColor-${this.dataset.hostProductId || 'host'}`;

    host.replaceChildren();

    const labelEl = document.createElement('p');
    labelEl.id = labelId;
    labelEl.className = 'personalize-charms__charm-color-label';
    labelEl.textContent = label;
    host.append(labelEl);
    host.setAttribute('role', 'group');
    host.setAttribute('aria-labelledby', labelId);

    const list = document.createElement('ul');
    list.className = 'personalize-charms__charm-color-swatches list-unstyled';
    list.setAttribute('role', 'list');

    for (const color of colors) {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'personalize-charms__charm-color-swatch';
      const selected = color === this.#activeColor;
      button.classList.toggle('personalize-charms__charm-color-swatch--selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-label', color);
      button.title = color;
      button.setAttribute('on:click', `/selectCharmColor/${encodeURIComponent(color)}`);

      const face = document.createElement('span');
      face.className = 'personalize-charms__charm-color-swatch-face';
      face.style.cssText = this.#charmColorSwatchStyle(color);
      face.setAttribute('aria-hidden', 'true');

      const name = document.createElement('span');
      name.className = 'personalize-charms__charm-color-swatch-name';
      name.textContent = color;

      button.append(face, name);
      li.append(button);
      list.append(li);
    }

    host.append(list);
  }

  /** Renders the charm tile grid for #activeColor, gating on variant.available (FR-14). Story 2.4. */
  #renderTileGrid() {
    const grid = this.refs.tileGrid;
    if (!(grid instanceof HTMLElement) || !this.#charmsProduct || !this.#activeColor) return;

    const atLimit = this.#inProgress.length >= this.#maxCharms();
    const oosLabel = this.#outOfStockLabel();
    const color = this.#activeColor;

    grid.replaceChildren();
    grid.setAttribute('role', 'list');
    grid.setAttribute('aria-busy', 'false');

    for (const symbol of CHARM_SYMBOL_ORDER) {
      const variant = this.#variantFor(symbol, color);
      const available = Boolean(variant?.available);
      const missing = !variant;
      const disabled = missing || !available || atLimit;

      const item = document.createElement('div');
      item.className = 'personalize-charms__tile';
      item.setAttribute('role', 'listitem');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'personalize-charms__tile-button';
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.setAttribute('aria-label', this.#tileAriaLabel(symbol, color, !missing && available));
      if (!disabled) {
        button.setAttribute('on:click', `/selectTile/${encodeURIComponent(symbol)}`);
      }
      if (missing || !available) {
        button.classList.add('personalize-charms__tile-button--sold-out');
      }
      if (atLimit) {
        button.classList.add('personalize-charms__tile-button--at-limit');
      }

      const media = document.createElement('span');
      media.className = 'personalize-charms__tile-media';
      media.setAttribute('aria-hidden', 'true');

      const src = variant?.featured_image?.src;
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 220;
        img.height = 220;
        media.append(img);
      } else {
        media.textContent = symbol;
      }

      button.append(media);

      const label = document.createElement('span');
      label.className = 'personalize-charms__tile-label';
      label.textContent = symbol;
      button.append(label);

      if (missing || !available) {
        const badge = document.createElement('span');
        badge.className = 'personalize-charms__tile-oos';
        badge.textContent = oosLabel;
        button.append(badge);
      }

      item.append(button);
      grid.append(item);
    }
  }

  #renderCharmsLoadError() {
    const grid = this.refs.tileGrid;
    if (!(grid instanceof HTMLElement)) return;
    grid.replaceChildren();
    grid.setAttribute('aria-busy', 'false');
    const message = document.createElement('p');
    message.className = 'personalize-charms__load-error';
    message.textContent = this.dataset.i18nLoadError || 'Unable to load charms. Please try again.';
    grid.append(message);
  }

  /**
   * Disables strap × buttons and strips `on:click` so rapid clicks cannot use
   * stale indices while a deferred `#syncInProgressUi` is pending. Leaves nodes
   * in the DOM (no detach) so the current click's event.target stays inside
   * `<dialog>` for DialogComponent's outside-click check.
   */
  #neutralizeStrapRemoveControls() {
    const strap = this.refs.strapPreview;
    if (!(strap instanceof HTMLElement)) return;
    const host = strap.querySelector('[data-strap-charms]');
    if (!(host instanceof HTMLElement)) return;
    for (const node of host.querySelectorAll('.personalize-charms__strap-charm-remove')) {
      if (!(node instanceof HTMLButtonElement)) continue;
      node.disabled = true;
      node.removeAttribute('on:click');
    }
  }

  /**
   * Defers DOM rebuilds until after the current click event finishes bubbling.
   * Prevents DialogComponent's outside-click handler from treating a detached
   * event.target (after replaceChildren) as "outside" and closing the modal.
   * Queues callbacks instead of replacing them so a later schedule cannot drop
   * an earlier post-commit card/enforce sync.
   * @param {() => void} fn
   */
  #scheduleUiSync(fn) {
    this.#uiSyncQueue.push(fn);
    if (this.#uiSyncTimer !== undefined) return;
    this.#uiSyncTimer = window.setTimeout(() => {
      this.#uiSyncTimer = undefined;
      const batch = this.#uiSyncQueue.splice(0);
      for (const queued of batch) queued();
    }, 0);
  }

  /**
   * Moves this block into the product form between the Wishlist app button and
   * the quantity / Add to cart row. Re-runs when wishlist injects late so order
   * stays: Wishlist → Personalize → quantity/Add to cart.
   */
  #placeBlockInBuyForm() {
    const section = this.closest('.shopify-section');
    const form =
      section?.querySelector('product-form-component form') ??
      section?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    const place = () => {
      const wishlistHost = form.querySelector('xb-wishlist-button');
      const formButtons = form.querySelector('.product-form-buttons');
      if (!wishlistHost && !formButtons) return false;

      if (wishlistHost) {
        // Correct order: wishlist, then this block, then form buttons.
        if (this.previousElementSibling !== wishlistHost) {
          wishlistHost.after(this);
        }
        this.dataset.placed = 'true';
        // Keep observing briefly in case wishlist remounts.
        return Boolean(formButtons);
      }

      if (formButtons && this.nextElementSibling !== formButtons && this !== formButtons.previousElementSibling) {
        formButtons.before(this);
        this.dataset.placed = 'true';
      }
      return false;
    };

    place();

    this.#placeObserver?.disconnect();
    this.#placeObserver = new MutationObserver(() => {
      if (place()) {
        // Wishlist + form buttons both present and ordered — stop watching.
        const wishlistHost = form.querySelector('xb-wishlist-button');
        if (wishlistHost && this.previousElementSibling === wishlistHost) {
          this.#placeObserver?.disconnect();
        }
      }
    });
    this.#placeObserver.observe(form, { childList: true, subtree: true });
    window.setTimeout(() => this.#placeObserver?.disconnect(), 8000);
  }

  /**
   * @param {string} color
   * @returns {{ option1?: string, option2?: string, option3?: string }}
   */
  #stubVariantForCollarColor(color) {
    const position = Number(this.dataset.collarColorOptionPosition || '1');
    /** @type {{ option1?: string, option2?: string, option3?: string }} */
    const stub = {};
    if (position === 1) stub.option1 = color;
    else if (position === 2) stub.option2 = color;
    else stub.option3 = color;
    return stub;
  }

  /**
   * Keeps strap preview, counter, Clear control, tile-grid limit gating, and
   * footer price / Add to Set state in sync with `#inProgress`.
   * Story 2.5 (FR-11, FR-12) + Story 2.4 limit gating + Story 2.6 (FR-15).
   */
  #syncInProgressUi() {
    this.#renderStrapPreview();
    this.#renderCounter();
    this.#renderTileGrid();
    this.#renderFooterPrice();
  }

  /** Renders the ordered strap preview of #inProgress. Story 2.5 (FR-11). */
  #renderStrapPreview() {
    const strap = this.refs.strapPreview;
    if (!(strap instanceof HTMLElement)) return;

    const host = strap.querySelector('[data-strap-charms]');
    if (!(host instanceof HTMLElement)) return;

    host.replaceChildren();

    if (this.#inProgress.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'personalize-charms__strap-empty';
      empty.textContent = this.dataset.i18nStrapEmpty || 'Tap a charm below to get started';
      host.append(empty);
      return;
    }

    const removeTemplate = this.dataset.i18nRemoveCharm || 'Remove {{ symbol }} charm';

    this.#inProgress.forEach((charm, index) => {
      const item = document.createElement('div');
      item.className = 'personalize-charms__strap-charm';
      item.dataset.symbol = charm.symbol;
      item.dataset.color = charm.color;

      if (charm.image) {
        const img = document.createElement('img');
        img.className = 'personalize-charms__strap-charm-media';
        img.src = charm.image;
        img.alt = `${charm.symbol}, ${charm.color}`;
        img.width = 40;
        img.height = 40;
        item.append(img);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'personalize-charms__strap-charm-fallback';
        fallback.textContent = charm.symbol;
        item.append(fallback);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'personalize-charms__strap-charm-remove';
      remove.textContent = '×';
      remove.setAttribute(
        'aria-label',
        removeTemplate.replaceAll('__SYMBOL__', charm.symbol).replaceAll('{{ symbol }}', charm.symbol)
      );
      remove.setAttribute('on:click', `/removeInProgressCharm/${index}`);
      item.append(remove);
      host.append(item);
    });
  }

  /** Updates `{current} / {max}` and Clear enabled state. Story 2.5 (FR-12). */
  #renderCounter() {
    const max = this.#maxCharms();
    const current = Math.min(this.#inProgress.length, max);

    const counter = this.refs.counter;
    if (counter instanceof HTMLElement) {
      counter.textContent = `${current} / ${max}`;
    }

    const clearButton = this.refs.clearButton;
    if (clearButton instanceof HTMLButtonElement) {
      clearButton.disabled = this.#inProgress.length === 0;
    }
  }

  /** Renders the footer price line and Add to Set enabled state. Story 2.6 (FR-15). */
  #renderFooterPrice() {
    const count = this.#inProgress.length;
    const perCharmCents = this.#perCharmPriceCents();
    const totalCents = count * perCharmCents;

    const priceFormatted = this.#formatCharmMoney(perCharmCents);
    const totalFormatted = this.#formatCharmMoney(totalCents);
    const template =
      this.dataset.i18nFooterPriceLine ||
      '{{ price }} PER CHARM | {{ count }} × {{ price }} = {{ total }}';

    const priceLine = this.refs.footerPriceLine;
    if (priceLine instanceof HTMLElement) {
      priceLine.textContent = template
        .replaceAll('__PRICE__', priceFormatted)
        .replaceAll('__COUNT__', String(count))
        .replaceAll('__TOTAL__', totalFormatted)
        .replaceAll('{{ price }}', priceFormatted)
        .replaceAll('{{ count }}', String(count))
        .replaceAll('{{ total }}', totalFormatted);
    }

    const canCommit = count >= 1;
    const addToSetButton = this.refs.addToSetButton;
    if (addToSetButton instanceof HTMLButtonElement) {
      addToSetButton.disabled = !canCommit;
      const helperId = this.refs.addToSetHelper?.id;
      if (canCommit) {
        addToSetButton.removeAttribute('aria-describedby');
      } else if (helperId) {
        addToSetButton.setAttribute('aria-describedby', helperId);
      }
    }

    const helper = this.refs.addToSetHelper;
    if (helper instanceof HTMLElement) {
      helper.hidden = canCommit;
      if (!helper.textContent?.trim()) {
        helper.textContent =
          this.dataset.i18nAddToSetDisabledHelper || 'Select at least 1 charm to add a set';
      }
    }
  }

  /**
   * Flat per-charm price in cents (FR-2). Prefers an in-progress charm; falls
   * back to the first Master Charms variant once the product JSON is loaded.
   * @returns {number}
   */
  #perCharmPriceCents() {
    const fromSelection = Number(this.#inProgress[0]?.price);
    if (Number.isFinite(fromSelection) && fromSelection >= 0) return fromSelection;

    const variants = this.#charmsProduct?.variants;
    if (Array.isArray(variants)) {
      for (const variant of variants) {
        const price = Number(variant?.price);
        if (Number.isFinite(price) && price >= 0) return price;
      }
    }

    return 0;
  }

  /**
   * Formats cents with the shop money format from Liquid data attrs.
   * @param {number} cents
   * @returns {string}
   */
  #formatCharmMoney(cents) {
    const amount = Number.isFinite(cents) ? cents : 0;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || '';
    return formatMoney(amount, format, currency);
  }

  /**
   * Syncs the modal Collar Color Preview + strap graphic from a variant-shaped
   * object (used on open from the PDP, and when tapping in-modal swatches).
   * Never writes back to the Host Product variant picker.
   * @param {object|null|undefined} variant
   */
  #syncCollarColorPreview(variant) {
    const color = this.#collarColorFromVariant(variant) ?? this.#readCurrentCollarColorFromPdp();
    if (!color) return;

    this.#collarColor = color;
    this.dataset.selectedCollarColor = color;

    const row = this.refs.colorPreviewRow;
    if (row instanceof HTMLElement) {
      const nameEl = row.querySelector('[data-collar-color-name]');
      if (nameEl) nameEl.textContent = color;

      for (const swatch of row.querySelectorAll('[data-collar-color]')) {
        if (!(swatch instanceof HTMLElement)) continue;
        const selected = swatch.dataset.collarColor === color;
        swatch.classList.toggle('personalize-charms__collar-swatch--selected', selected);
        swatch.setAttribute('aria-pressed', selected ? 'true' : 'false');
      }
    }

    const strap = this.refs.strapPreview;
    if (strap instanceof HTMLElement) {
      strap.dataset.collarColor = color;
      const selectedSwatch = row?.querySelector(
        `[data-collar-color="${CSS.escape(color)}"] .swatch`
      );
      if (selectedSwatch instanceof HTMLElement) {
        const bg = getComputedStyle(selectedSwatch).getPropertyValue('--swatch-background').trim();
        if (bg) strap.style.setProperty('--collar-color', bg);
      }
    }
  }

  /**
   * @param {object|null|undefined} variant
   * @returns {string|undefined}
   */
  #collarColorFromVariant(variant) {
    if (!variant || typeof variant !== 'object') return undefined;
    const position = Number(this.dataset.collarColorOptionPosition || '1');
    if (position >= 1 && position <= 3) {
      const keyed = /** @type {Record<string, unknown>} */ (variant)[`option${position}`];
      if (typeof keyed === 'string' && keyed) return keyed;
    }
    const options = /** @type {{ options?: string[] }} */ (variant).options;
    if (Array.isArray(options) && typeof options[position - 1] === 'string') {
      return options[position - 1];
    }
    return undefined;
  }

  /**
   * Best-effort read of the Host Product's currently checked Color radio on the PDP.
   * @returns {string|undefined}
   */
  #readCurrentCollarColorFromPdp() {
    const section = this.closest('.shopify-section');
    if (!section) return this.dataset.selectedCollarColor || undefined;

    // Horizon names radios `{OptionName}-{blockId}-{productId}`, not `options[Color]`.
    const picker =
      section.querySelector(
        `variant-picker[data-product-id="${CSS.escape(String(this.#hostProductId ?? ''))}"]`
      ) ?? section.querySelector('variant-picker');

    const checked = picker?.querySelector('input[type="radio"][name^="Color-"]:checked');
    if (checked instanceof HTMLInputElement && checked.value) return checked.value;
    return this.dataset.selectedCollarColor || undefined;
  }

  /**
   * Build a minimal variant-shaped object from the live PDP selection so openModal
   * can sync even when no VariantUpdateEvent has fired yet this session.
   * @returns {{ option1?: string, option2?: string, option3?: string }|null}
   */
  #readCurrentVariantFromPdp() {
    const color = this.#readCurrentCollarColorFromPdp();
    if (!color) return null;
    const position = Number(this.dataset.collarColorOptionPosition || '1');
    /** @type {{ option1?: string, option2?: string, option3?: string }} */
    const stub = {};
    if (position === 1) stub.option1 = color;
    else if (position === 2) stub.option2 = color;
    else if (position === 3) stub.option3 = color;
    return stub;
  }

  /**
   * Renders one Set Summary Card per committed Charm Set under the Trigger.
   * Degraded vs normal state uses `set.cartReadyAtCommit` only (AD-7) — never
   * re-reads `dataset.personalizeCartReady` at paint time. Story 2.7 (FR-16).
   */
  #renderSetSummaryList() {
    const list = this.refs.setSummaryList;
    if (!(list instanceof HTMLElement)) return;

    list.replaceChildren();

    const labelTemplate =
      this.dataset.i18nSetCardLabel || 'Charm Set — {{ count }} charm{{ s }}';
    const deleteLabel = this.dataset.i18nSetCardDelete || 'Delete';
    const degradedWarning =
      this.dataset.i18nSetCardDegradedWarning ||
      'This set could not be confirmed — please refresh the page before checking out to make sure your charms are included.';

    this.#committedSets.forEach((set, setIndex) => {
      const count = set.charms.length;
      const plural = count === 1 ? '' : 's';
      const label = labelTemplate
        .replaceAll('__COUNT__', String(count))
        .replaceAll('{{ count }}', String(count))
        .replaceAll('__S__', plural)
        .replaceAll('{{ s }}', plural);

      const card = document.createElement('article');
      card.className = 'personalize-charms__set-summary-card';
      card.dataset.setId = set.setId;
      const labelId = `PersonalizeCharmsSetLabel-${this.#hostProductId ?? 'host'}-${setIndex}`;
      const warningId = `PersonalizeCharmsSetWarning-${this.#hostProductId ?? 'host'}-${setIndex}`;

      const body = document.createElement('div');
      body.className = 'personalize-charms__set-summary-body';

      const main = document.createElement('div');
      main.className = 'personalize-charms__set-summary-main';

      const preview = document.createElement('div');
      preview.className = 'personalize-charms__set-summary-preview';
      preview.setAttribute('aria-hidden', 'true');

      set.charms.forEach((charm) => {
        const item = document.createElement('div');
        item.className = 'personalize-charms__set-summary-charm';

        if (charm.image) {
          const img = document.createElement('img');
          img.className = 'personalize-charms__strap-charm-media';
          img.src = charm.image;
          img.alt = '';
          img.width = 26;
          img.height = 26;
          item.append(img);
        } else {
          const fallback = document.createElement('span');
          fallback.className = 'personalize-charms__strap-charm-fallback';
          fallback.textContent = charm.symbol;
          item.append(fallback);
        }

        preview.append(item);
      });

      const meta = document.createElement('div');
      meta.className = 'personalize-charms__set-summary-meta';

      const labelEl = document.createElement('p');
      labelEl.className = 'personalize-charms__set-summary-label';
      labelEl.id = labelId;
      labelEl.textContent = label;

      const priceEl = document.createElement('p');
      priceEl.className = 'personalize-charms__set-summary-price';
      priceEl.textContent = this.#formatCharmMoney(set.total);

      meta.append(labelEl, priceEl);
      main.append(preview, meta);
      body.append(main);

      const describedBy = [labelId];

      if (!set.cartReadyAtCommit) {
        card.classList.add('personalize-charms__set-summary-card--degraded');
        const warning = document.createElement('p');
        warning.className = 'personalize-charms__set-summary-warning';
        warning.id = warningId;
        warning.textContent = degradedWarning;
        body.append(warning);
        describedBy.push(warningId);
      }

      card.setAttribute('aria-labelledby', labelId);
      if (describedBy.length > 1) {
        card.setAttribute('aria-describedby', describedBy.slice(1).join(' '));
      }

      const deleteButton = document.createElement('button');
      deleteButton.type = 'button';
      deleteButton.className = 'button button-secondary personalize-charms__set-summary-delete';
      deleteButton.textContent = deleteLabel;
      deleteButton.setAttribute('aria-label', `${deleteLabel}: ${label}`);
      deleteButton.setAttribute('on:click', `/deleteSet/${encodeURIComponent(set.setId)}`);

      card.append(body, deleteButton);
      list.append(card);
    });
  }

  /** Disables the trigger with explanatory text once Max Sets Limit is reached. Story 2.8 (FR-18). */
  #enforceMaxSetsLimit() {
    const trigger = this.refs.triggerButton;
    if (!(trigger instanceof HTMLButtonElement)) return;

    // FR-30 wins: Liquid already disabled + labeled the trigger.
    if (this.dataset.charmsAvailable === 'false') return;

    const max = this.#maxSets();
    const atLimit = this.#committedSets.length >= max;
    const helper = this.refs.maxSetsHelper;
    const maxLabel = String(max);
    const template =
      this.dataset.i18nMaxSetsHelper ||
      'You have reached the maximum of {{ max }} charm sets. Delete a set below to add another.';
    const ctaText = this.dataset.ctaText || '';

    trigger.disabled = atLimit;
    if (ctaText) trigger.textContent = ctaText;

    if (helper instanceof HTMLElement) {
      helper.textContent = template
        .replaceAll('__MAX__', maxLabel)
        .replaceAll('{{ max }}', maxLabel);
      helper.hidden = !atLimit;
      if (atLimit && helper.id) {
        trigger.setAttribute('aria-describedby', helper.id);
      } else {
        trigger.removeAttribute('aria-describedby');
      }
    }
  }

  /**
   * AD-7 liveness handshake: personalize-cart.js sets this marker synchronously
   * at module-load time, before any user interaction is possible. If it's
   * missing, the just-committed set must be rendered in a degraded
   * "not guaranteed to be added" state instead of a normal success state —
   * never a silent false confirmation. Implemented by Story 2.6/2.7, which
   * are the first stories to actually commit a set.
   */
  #dispatchSetsChanged() {
    const isCartModuleReady = document.documentElement.dataset.personalizeCartReady === 'true';
    const setsSnapshot = this.#committedSets.map((set) => ({
      ...set,
      charms: set.charms.map((charm) => ({ ...charm })),
    }));
    document.dispatchEvent(
      new CustomEvent('personalize:sets-changed', {
        detail: { hostProductId: this.#hostProductId, sets: setsSnapshot },
      })
    );
    return isCartModuleReady;
  }
}

if (!customElements.get('personalize-charms-component')) {
  customElements.define('personalize-charms-component', PersonalizeCharmsComponent);
}
